import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { setConditionField } from 'actions/feature/conditions'
import { updateInputConfigs, switchInputType } from 'actions/feature/inputs'

import { useSelector } from 'hooks/redux'
import { getScalingQueryProps } from 'selectors/feature/scalingQueries'
import {
  getConfiguredInputs,
  getSignalTranslation,
  getDefaultInputConfigurations,
} from 'selectors/feature/inputs'

import strings from 'utils/strings'
import { QUERY_INPUT_TYPES, CHEC_MAX_VALUE, BOOLEAN } from 'utils/constants'

import { DEFAULT_NUM_BITS } from 'constants/conditions'

import { getInputConfig } from 'components/feature/utils/action-helpers'

import InputConfigEditor from 'components/feature/NavigatorPanes/editors/base/InputConfigEditor/InputConfigEditor'
import { RadioButtonGroup } from 'components/patterns'
import InputValueEditor from 'components/feature/NavigatorPanes/editors/base/InputValueEditor/InputValueEditor'
import CanInputEditor from 'components/feature/NavigatorPanes/editors/CanInputEditor/CanInputEditor'

import scalingQuerySchema from 'schemas/validation/feature/scalingQuerySchema'
import { schemaProp } from 'schemas/validation'

import InputSelector from './InputSelector'

const rule = schemaProp(scalingQuerySchema)

const { NATIVE_INPUT, CAN_INPUT, ANALOG_INPUT } = QUERY_INPUT_TYPES

const inputTypes = [
  {
    text: strings.native,
    value: NATIVE_INPUT,
  },
  {
    text: strings.analog_pin_input,
    value: ANALOG_INPUT,
  },
  {
    text: strings.can,
    value: CAN_INPUT,
  },
]

const ScalingInput = ({ uri, outputId }) => {
  const dispatch = useDispatch()

  const scalingQueryProps = useSelector(getScalingQueryProps, { uri })
  const {
    signal: signalUri,
    subType,
    inputConfig,
    valueMin,
    valueMax,
    startBit,
    numberOfBits,
    canId,
  } = scalingQueryProps

  const setField = (prop) => (value) =>
    dispatch(setConditionField(uri, prop, value))

  const valueMinChange = setField('valueMin')
  const valueMaxChange = setField('valueMax')
  const signalChange = setField('signal')
  const setInputConfig = setField('inputConfig')
  const numberOfBitsChange = setField('numberOfBits')
  const startBitChange = setField('startBit')

  /* start for inputConfigEditor */
  const configuredInputs = useSelector(getConfiguredInputs)

  const { conditionsSharingConfig, description: selectedInputDescription } =
    configuredInputs[signalUri] || {}

  const handleInputConfigChange = (config) =>
    dispatch(updateInputConfigs(config, conditionsSharingConfig))

  // if multiple queries have same config, we must warn the user before making the change
  const requirePrompt =
    conditionsSharingConfig && conditionsSharingConfig.length > 1
  /* end for inputConfigEditor */

  const translation = useSelector(getSignalTranslation, {
    signal: signalUri,
    inputConfig,
  })
  const {
    minValue = 0,
    maxValue = CHEC_MAX_VALUE,
    units,
    translations,
    type: translationType,
  } = translation

  // needed to determine newInputConfig when changing signalUri
  const defaultInputConfigurations = useSelector(getDefaultInputConfigurations)

  // TODO: do these in a saga ?
  const handleSignalChange = (newSignalUri) => {
    const newInputConfig = getInputConfig(
      configuredInputs,
      defaultInputConfigurations,
      newSignalUri
    )

    setInputConfig(newInputConfig)
    signalChange(newSignalUri)
    valueMinChange(null)
    valueMaxChange(null)
  }

  // For boolean signals, valueMax is not shown but must stay in sync with
  // the complementary boolean value so schema validation passes on save.
  const handleBooleanValueMinChange = (newValueMin) => {
    valueMinChange(newValueMin)
    const complement = translations?.find(
      // eslint-disable-next-line eqeqeq
      (t) => t.value != newValueMin
    )
    if (complement !== undefined) valueMaxChange(complement.value)
  }

  const handleInputTypeChange = (event) => {
    dispatch(
      switchInputType(uri, event.target.value, {
        defaultNumberOfBits: DEFAULT_NUM_BITS.SCALING_INPUT,
      })
    )
  }

  return (
    <>
      <RadioButtonGroup
        label={'Scaling Input Type'}
        buttons={inputTypes}
        currentValue={subType}
        onChange={handleInputTypeChange}
        vertical
      />

      {subType !== 'can' && (
        <InputSelector
          onInputChange={handleSignalChange}
          rungName={subType}
          selectedType={subType}
          signalUri={signalUri}
          outputId={outputId}
        />
      )}

      {subType === 'can' && (
        <CanInputEditor
          canId={canId}
          uri={uri}
          startBit={startBit}
          numberOfBits={numberOfBits}
          onNumberOfBitsChange={(e) => numberOfBitsChange(e.target.value)}
          onStartBitChange={startBitChange}
          hasSixteenBitOutput={true} // TODO: change to some other var
        />
      )}

      {(signalUri || canId) && (
        <>
          {subType === 'analogInput' && (
            <InputConfigEditor
              dataType={subType}
              selectedInputDescription={selectedInputDescription}
              inputConfig={inputConfig}
              onInputConfigChange={handleInputConfigChange}
              requirePrompt={requirePrompt}
              disabledConfigs={[]}
            />
          )}

          <InputValueEditor
            value={valueMin}
            onInputValueChange={
              translationType === BOOLEAN
                ? handleBooleanValueMinChange
                : valueMinChange
            }
            customRule={rule('valueMin', {
              minValue,
              maxValue,
              valueMax,
            })}
            translations={translations}
            minValue={minValue}
            maxValue={maxValue}
            inputConfig={inputConfig}
            units={units}
            translationType={translationType}
            makeLabel={(label) => `${strings.scaling_input_min} ${label}`}
            name={strings.scaling_input_min}
          />

          {translationType !== BOOLEAN && (
            <InputValueEditor
              value={valueMax}
              onInputValueChange={valueMaxChange}
              customRule={rule('valueMax', {
                minValue,
                maxValue,
                valueMin,
              })}
              translations={translations}
              minValue={minValue}
              maxValue={maxValue}
              inputConfig={inputConfig}
              units={units}
              translationType={translationType}
              makeLabel={(label) => `${strings.scaling_input_max} ${label}`}
              name={strings.scaling_input_max}
            />
          )}
        </>
      )}
    </>
  )
}

ScalingInput.propTypes = {
  uri: PropTypes.string,
  outputId: PropTypes.string,
}

export default ScalingInput
